<template>
  <div class="at-jobs">
    <section class="at-jobs__intro">
      <div class="at-jobs__intro-icon">
        <feather-icon
          icon="BriefcaseIcon"
          size="22"
        />
      </div>
      <div>
        <h3 class="at-jobs__intro-title">
          Audit Jobs & Team
        </h3>
        <p class="at-jobs__intro-text">
          Create audit job roles and assign team members. Open a job card to manage its team.
        </p>
      </div>
      <button
        type="button"
        class="at-jobs__add-btn"
        @click="openCreateModal"
      >
        <feather-icon
          icon="PlusIcon"
          size="16"
        />
        Add Job
      </button>
    </section>

    <section class="at-jobs__toolbar">
      <div class="at-jobs__stat">
        <span class="at-jobs__stat-value">{{ jobs.length }}</span>
        <span class="at-jobs__stat-label">{{ jobs.length === 1 ? 'Job' : 'Jobs' }}</span>
      </div>
      <div class="ar-search at-jobs__search">
        <feather-icon
          icon="SearchIcon"
          size="15"
          class="ar-search__icon"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="ar-search__input"
          placeholder="Search jobs by name…"
        >
        <button
          v-if="searchQuery"
          type="button"
          class="ar-search__clear"
          @click="searchQuery = ''"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
      </div>
    </section>

    <div
      v-if="dataLoad"
      class="ar-loading"
    >
      <div class="ar-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading audit jobs…
      </p>
    </div>

    <div
      v-else-if="!filteredJobs.length"
      class="ar-empty at-jobs__empty"
    >
      <div class="ar-empty__icon">
        <feather-icon
          icon="InboxIcon"
          size="28"
        />
      </div>
      <h5 class="mb-50">
        {{ searchQuery ? 'No matching jobs' : 'No audit jobs yet' }}
      </h5>
      <p class="mb-0 text-muted">
        {{ searchQuery ? 'Try a different search.' : 'Create your first audit job to get started.' }}
      </p>
      <button
        v-if="!searchQuery"
        type="button"
        class="at-jobs__add-btn at-jobs__add-btn--inline"
        @click="openCreateModal"
      >
        <feather-icon
          icon="PlusIcon"
          size="16"
        />
        Add Job
      </button>
    </div>

    <div
      v-else
      class="at-jobs__grid"
    >
      <article
        v-for="(job, index) in filteredJobs"
        :key="job.id"
        class="at-job-card"
        :class="`at-job-card--${index % 4}`"
      >
        <div class="at-job-card__top">
          <div
            class="at-job-card__icon"
            :class="`at-job-card__icon--${index % 4}`"
          >
            <feather-icon
              icon="LayersIcon"
              size="18"
            />
          </div>
          <div class="at-job-card__actions">
            <button
              type="button"
              class="at-job-card__action at-job-card__action--edit"
              title="Edit job"
              @click.stop="openEditModal(job)"
            >
              <feather-icon
                icon="Edit2Icon"
                size="15"
              />
            </button>
            <button
              type="button"
              class="at-job-card__action at-job-card__action--delete"
              title="Delete job"
              :disabled="deleteLoadId === job.id"
              @click.stop="confirmDelete(job)"
            >
              <b-spinner
                v-if="deleteLoadId === job.id"
                small
                variant="danger"
              />
              <feather-icon
                v-else
                icon="Trash2Icon"
                size="15"
              />
            </button>
          </div>
        </div>

        <h4 class="at-job-card__name">
          {{ job.name }}
        </h4>

        <div class="at-job-card__preview">
          <div
            v-if="memberCount(job.id)"
            class="at-job-card__avatars"
          >
            <span
              v-for="(member, memberIndex) in previewMembers(job.id)"
              :key="`${job.id}-preview-${member.id || member.user_id}-${memberIndex}`"
              class="at-job-card__avatar"
              :class="`at-job-card__avatar--${memberIndex % 4}`"
              :title="member.name"
            >
              {{ avatarText(member.name) }}
            </span>
            <span
              v-if="memberCount(job.id) > 4"
              class="at-job-card__avatar at-job-card__avatar--more"
            >
              +{{ memberCount(job.id) - 4 }}
            </span>
          </div>
          <p
            v-else
            class="at-job-card__empty-team"
          >
            No members assigned
          </p>
        </div>

        <div class="at-job-card__footer">
          <span class="at-job-card__meta">
            <feather-icon
              icon="UsersIcon"
              size="12"
            />
            {{ memberCount(job.id) }} {{ memberCount(job.id) === 1 ? 'member' : 'members' }}
          </span>
          <button
            type="button"
            class="at-job-card__manage"
            @click="openTeamModal(job)"
          >
            <feather-icon
              icon="UserCheckIcon"
              size="14"
            />
            Manage Team
          </button>
        </div>
      </article>
    </div>

    <b-modal
      v-model="teamModal"
      size="lg"
      centered
      hide-footer
      modal-class="at-team-modal"
      content-class="at-team-modal__content"
      @hidden="resetTeamModal"
    >
      <template #modal-title>
        <div
          v-if="teamModalJob"
          class="at-team-modal__title"
        >
          <feather-icon
            icon="BriefcaseIcon"
            size="18"
            class="mr-50"
          />
          {{ teamModalJob.name }}
          <span class="at-team-modal__subtitle">Team Members</span>
        </div>
      </template>

      <div
        v-if="teamModalJob"
        class="at-job-panel__team at-job-panel__team--modal"
      >
        <div class="at-job-panel__add">
          <v-select
            v-model="selectedUserByJob[teamModalJob.id]"
            :options="availableUsersForJob(teamModalJob.id)"
            :disabled="isAddLoad(teamModalJob.id) || usersLoading"
            :loading="usersLoading"
            placeholder="Search user by name or email…"
            class="at-job-panel__select"
          >
            <template #no-options>
              No more users available for this job
            </template>
          </v-select>
          <button
            type="button"
            class="at-job-panel__add-btn"
            :disabled="!selectedUserByJob[teamModalJob.id] || isAddLoad(teamModalJob.id)"
            @click="addJobUser(teamModalJob)"
          >
            <b-spinner
              v-if="isAddLoad(teamModalJob.id)"
              small
              variant="light"
            />
            <template v-else>
              <feather-icon
                icon="UserPlusIcon"
                size="15"
              />
              Add Member
            </template>
          </button>
        </div>

        <div
          v-if="isUsersLoad(teamModalJob.id)"
          class="at-job-panel__members-loading"
        >
          <b-spinner
            small
            variant="primary"
          />
          <span>Loading team…</span>
        </div>

        <div
          v-else-if="!getJobUsers(teamModalJob.id).length"
          class="at-job-panel__members-empty"
        >
          <feather-icon
            icon="UserXIcon"
            size="20"
          />
          <p>No members assigned yet. Add someone using the field above.</p>
        </div>

        <ul
          v-else
          class="at-job-panel__members"
        >
          <li
            v-for="(member, memberIndex) in getJobUsers(teamModalJob.id)"
            :key="`${teamModalJob.id}-${member.id || member.user_id}-${memberIndex}`"
            class="at-member"
            :class="`at-member--${memberIndex % 4}`"
          >
            <div
              class="at-member__avatar"
              :class="`at-member__avatar--${memberIndex % 4}`"
            >
              {{ avatarText(member.name) }}
            </div>
            <div class="at-member__body">
              <span class="at-member__name">{{ member.name }}</span>
              <a
                v-if="member.email"
                :href="`mailto:${member.email}`"
                class="at-member__email"
              >
                <feather-icon
                  icon="MailIcon"
                  size="12"
                />
                {{ member.email }}
              </a>
            </div>
            <div class="at-member__actions">
              <button
                type="button"
                class="at-member__btn at-member__btn--remove"
                title="Remove member"
                :disabled="removeLoadId === member.id"
                @click="confirmRemoveUser(teamModalJob, member)"
              >
                <b-spinner
                  v-if="removeLoadId === member.id"
                  small
                  variant="danger"
                />
                <feather-icon
                  v-else
                  icon="UserMinusIcon"
                  size="14"
                />
              </button>
            </div>
          </li>
        </ul>
      </div>
    </b-modal>

    <b-modal
      v-model="formModal"
      centered
      hide-footer
      modal-class="at-job-modal"
      content-class="at-job-modal__content"
      @hidden="resetForm"
    >
      <template #modal-title>
        <div class="at-job-modal__title">
          <feather-icon
            :icon="isEditMode ? 'Edit2Icon' : 'PlusCircleIcon'"
            size="18"
            class="mr-50"
          />
          {{ isEditMode ? 'Edit Job' : 'Add New Job' }}
        </div>
      </template>

      <div class="at-job-modal__body">
        <div class="at-job-form__field">
          <label class="at-job-form__label">
            <feather-icon
              icon="TagIcon"
              size="14"
              class="mr-25"
            />
            Job Name
            <span class="text-danger">*</span>
          </label>
          <b-form-input
            v-model="form.name"
            placeholder="e.g. Lead Auditor"
            :state="formTouched && !form.name.trim() ? false : null"
          />
          <small
            v-if="formTouched && !form.name.trim()"
            class="text-danger"
          >
            Job name is required
          </small>
        </div>
      </div>

      <div class="at-job-modal__footer">
        <button
          type="button"
          class="at-job-form__btn at-job-form__btn--cancel"
          :disabled="saveLoad"
          @click="formModal = false"
        >
          Cancel
        </button>
        <button
          type="button"
          class="at-job-form__btn at-job-form__btn--save"
          :disabled="saveLoad"
          @click="submitForm"
        >
          <b-spinner
            v-if="saveLoad"
            small
            variant="light"
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="15"
            class="mr-50"
          />
          {{ saveLoad ? 'Saving…' : (isEditMode ? 'Update Job' : 'Create Job') }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import VSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import formatDate from '@/global-compositions/formatDate'

export default {
  name: 'AuditTeamTab',
  components: {
    'v-select': VSelect,
  },
  props: {
    usersLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      jobs: [],
      jobUsers: {},
      jobUsersLoad: {},
      selectedUserByJob: {},
      addLoadByJob: {},
      teamModal: false,
      teamModalJob: null,
      dataLoad: false,
      saveLoad: false,
      deleteLoadId: null,
      removeLoadId: null,
      searchQuery: '',
      formModal: false,
      isEditMode: false,
      editingId: null,
      formTouched: false,
      form: {
        name: '',
      },
    }
  },
  computed: {
    filteredJobs() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.jobs
      return this.jobs.filter(job => (
        String(job.name || '').toLowerCase().includes(q)
      ))
    },
    assignList() {
      return this.$store.getters['generalData/assignList/assignList'] || []
    },
  },
  mounted() {
    this.fetchJobs()
  },
  methods: {
    formatDateValue(value) {
      return formatDate(value) || value
    },
    avatarText(name) {
      if (!name) return '?'
      return String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('')
    },
    normalizeList(payloadData) {
      return this.parseListPage(payloadData).items
    },
    parseListPage(payloadData) {
      if (!payloadData) return { items: [], lastPage: 1 }

      if (Array.isArray(payloadData)) {
        return { items: payloadData, lastPage: 1 }
      }

      if (Array.isArray(payloadData.users)) {
        return { items: payloadData.users, lastPage: 1 }
      }

      const paginated = payloadData.data
      if (paginated && Array.isArray(paginated.data)) {
        return {
          items: paginated.data,
          lastPage: Number(paginated.last_page) || 1,
        }
      }

      if (Array.isArray(payloadData.data)) {
        return { items: payloadData.data, lastPage: 1 }
      }

      const nestedArray = Object.values(payloadData).find(Array.isArray)
      return { items: nestedArray || [], lastPage: 1 }
    },
    normalizeJobUser(item) {
      const user = item?.user || {}
      return {
        id: item.id,
        user_id: item.user_id ?? user.id,
        name: item.name ?? user.name,
        email: item.email ?? user.email,
        created_at: item.created_at ?? user.created_at,
      }
    },
    getJobUsers(jobId) {
      return this.jobUsers[jobId] || []
    },
    memberCount(jobId) {
      return this.getJobUsers(jobId).length
    },
    previewMembers(jobId) {
      return this.getJobUsers(jobId).slice(0, 4)
    },
    isUsersLoad(jobId) {
      return Boolean(this.jobUsersLoad[jobId])
    },
    isAddLoad(jobId) {
      return Boolean(this.addLoadByJob[jobId])
    },
    existingUserIdsForJob(jobId) {
      return new Set(
        this.getJobUsers(jobId).map(item => Number(item.user_id)).filter(Boolean),
      )
    },
    availableUsersForJob(jobId) {
      const assigned = this.existingUserIdsForJob(jobId)
      return this.assignList
        .filter(user => !assigned.has(Number(user?.value?.id)))
        .map(user => ({
          value: user.value.id,
          label: user.label,
        }))
    },
    async fetchJobs() {
      this.dataLoad = true
      try {
        const response = await axios.get('/iso/jobs', {
          params: { per_page: 500 },
        })
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          this.jobs = await this.fetchAllPages(
            '/iso/jobs',
            responseData?.data ?? responseData,
          )
          await this.loadAllJobUsers()
        }
      } catch (error) {
        this.jobs = []
        this.jobUsers = {}
        this.showError(error, 'Failed to load audit jobs')
      } finally {
        this.dataLoad = false
      }
    },
    async loadAllJobUsers() {
      await Promise.all(this.jobs.map(job => this.fetchJobUsers(job.id, { silent: true })))
    },
    openTeamModal(job) {
      this.teamModalJob = job
      this.teamModal = true
      if (!this.jobUsers[job.id] && !this.isUsersLoad(job.id)) {
        this.fetchJobUsers(job.id)
      }
    },
    resetTeamModal() {
      this.teamModalJob = null
    },
    async fetchAllPages(url, firstPayload, { perPage = 500 } = {}) {
      const { items: firstItems, lastPage } = this.parseListPage(firstPayload)
      let items = [...firstItems]

      if (lastPage <= 1) return items

      const pageNumbers = Array.from({ length: lastPage - 1 }, (_, index) => index + 2)
      const pageResponses = await Promise.all(
        pageNumbers.map(page => axios.get(url, {
          params: { page, per_page: perPage },
        })),
      )

      pageResponses.forEach(response => {
        if (response.status !== 200) return
        const responseData = decryptData(response.data.payload)
        const { items: pageItems } = this.parseListPage(responseData?.data ?? responseData)
        items = items.concat(pageItems)
      })

      return items
    },
    async fetchJobUsers(jobId, { silent = false } = {}) {
      if (!jobId) return
      this.$set(this.jobUsersLoad, jobId, true)
      try {
        const response = await axios.get(`/iso/jobs/${jobId}/users`, {
          params: { per_page: 500 },
        })
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const users = await this.fetchAllPages(
            `/iso/jobs/${jobId}/users`,
            responseData?.data ?? responseData,
          )
          this.$set(
            this.jobUsers,
            jobId,
            users.map(item => this.normalizeJobUser(item)),
          )
        }
      } catch (error) {
        this.$set(this.jobUsers, jobId, [])
        if (!silent) {
          this.showError(error, 'Failed to load team members')
        }
      } finally {
        this.$set(this.jobUsersLoad, jobId, false)
      }
    },
    fetchMembers() {
      return this.fetchJobs()
    },
    refresh() {
      return this.fetchJobs()
    },
    openCreateModal() {
      this.isEditMode = false
      this.editingId = null
      this.form = { name: '' }
      this.formTouched = false
      this.formModal = true
    },
    openEditModal(job) {
      this.isEditMode = true
      this.editingId = job.id
      this.form = {
        name: job.name || '',
      }
      this.formTouched = false
      this.formModal = true
    },
    resetForm() {
      this.isEditMode = false
      this.editingId = null
      this.form = { name: '' }
      this.formTouched = false
      this.saveLoad = false
    },
    async submitForm() {
      this.formTouched = true
      if (!this.form.name.trim()) return

      this.saveLoad = true
      try {
        const payload = { name: this.form.name.trim() }
        let response
        if (this.isEditMode) {
          response = await axios.put(`/iso/jobs/${this.editingId}`, payload)
        } else {
          response = await axios.post('/iso/jobs', payload)
        }

        if ([200, 201].includes(response.status)) {
          this.formModal = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.isEditMode ? 'Job updated successfully' : 'Job created successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          await this.fetchJobs()
        }
      } catch (error) {
        this.showError(error, this.isEditMode ? 'Failed to update job' : 'Failed to create job')
      } finally {
        this.saveLoad = false
      }
    },
    async addJobUser(job) {
      const selection = this.selectedUserByJob[job.id]
      const userId = selection?.value
      if (!userId || this.isAddLoad(job.id)) return

      this.$set(this.addLoadByJob, job.id, true)
      try {
        const response = await axios.post(`/iso/jobs/${job.id}/users`, {
          user_id: userId,
        })
        if (response.status === 200 || response.status === 201) {
          this.$set(this.selectedUserByJob, job.id, null)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Member added to job',
              icon: 'UserPlusIcon',
              variant: 'success',
            },
          })
          await this.fetchJobUsers(job.id)
        }
      } catch (error) {
        this.showError(error, 'Failed to add team member')
      } finally {
        this.$set(this.addLoadByJob, job.id, false)
      }
    },
    confirmRemoveUser(job, member) {
      this.$swal({
        title: 'Remove this member?',
        text: member.name,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ea5455',
        cancelButtonColor: '#82868b',
      }).then(result => {
        if (result.isConfirmed) {
          this.removeJobUser(job, member)
        }
      })
    },
    async removeJobUser(job, member) {
      this.removeLoadId = member.id
      try {
        const response = await axios.delete(`/iso/job-users/${member.id}`)
        if (response.status === 200 || response.status === 204) {
          const list = this.getJobUsers(job.id).filter(item => item.id !== member.id)
          this.$set(this.jobUsers, job.id, list)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Member removed from job',
              icon: 'UserMinusIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        this.showError(error, 'Failed to remove team member')
      } finally {
        this.removeLoadId = null
      }
    },
    confirmDelete(job) {
      this.$swal({
        title: 'Delete this job?',
        text: job.name,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ea5455',
        cancelButtonColor: '#82868b',
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteJob(job)
        }
      })
    },
    async deleteJob(job) {
      this.deleteLoadId = job.id
      try {
        const response = await axios.delete(`/iso/jobs/${job.id}`)
        if (response.status === 200 || response.status === 204) {
          this.jobs = this.jobs.filter(item => item.id !== job.id)
          this.$delete(this.jobUsers, job.id)
          this.$delete(this.selectedUserByJob, job.id)
          if (this.teamModalJob?.id === job.id) {
            this.teamModal = false
            this.teamModalJob = null
          }
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Job deleted',
              icon: 'Trash2Icon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        this.showError(error, 'Failed to delete job')
      } finally {
        this.deleteLoadId = null
      }
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
          else if (typeof msg === 'object') {
            const firstList = Object.values(msg).find(Array.isArray)
            if (firstList) {
              const [firstMessage] = firstList
              if (firstMessage) message = firstMessage
            }
          }
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" src="../../AuditTeam.scss"></style>
